<template>
    <ion-content class="text-selectable modal-content">
        <div class="content-wrapper">
            <div class="scrollable-content">
                <div class="info-section">
                    <div class="notification-header">
                        <div class="notification-icon">
                            <font-awesome-icon :icon="['fas', 'bell']" />
                        </div>
                        <div class="notification-details">
                            <h2 class="notification-title">{{t('notifications-modal.title')}}</h2>
                            <div class="notification-subtitle">Push Notifications</div>
                        </div>
                    </div>
                </div>

                <div class="status-section" v-if="hasPermissions === false">
                    <div class="status-card status-card--red">
                        <div class="status-content">
                            <div class="status-title">{{ t("error-messages.no-notifications-permissions") }}</div>
                            <div class="status-subtitle">Permission Required</div>
                        </div>
                    </div>
                </div>

                <div class="details-section">
                    <div class="details-card">
                        <div class="detail-item">
                            <h4>{{ t('notifications-modal.content[0]') }}</h4>
                        </div>
                        <div class="detail-item">
                            <p>{{ t('notifications-modal.content[1]') }}</p>
                        </div>
                        <div class="detail-item">
                            <h4>Features:</h4>
                            <ul class="feature-list">
                                <li>{{ t('notifications-modal.list[0]') }}</li>
                                <li>{{ t('notifications-modal.list[1]') }}</li>
                            </ul>
                        </div>
                        <div class="detail-item">
                            <p>{{ t('notifications-modal.content[2]') }}</p>
                        </div>
                    </div>
                </div>

                <div class="toggle-section">
                    <div class="toggle-card">
                        <div class="toggle-content">
                            <div class="toggle-label">Enable Notifications</div>
                            <ion-toggle :disabled="hasPermissions === false" @ionChange="enableNotificationsChange" :checked="notificationsInfo?.enabled"></ion-toggle>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </ion-content>
</template>
  
<script lang="ts">
import { IonContent, IonToggle, isPlatform } from '@ionic/vue';
import { defineComponent, ref, onMounted, onBeforeUnmount } from "vue";
import { useI18n } from "vue-i18n";
import { useUserTalent } from "@/hooks/talent";
import errorHandlerService from "@/services/error-handler.service";
import notificationsService, {
    NotificationsInfo,
} from "@/services/notifications.service";
export default defineComponent({
    name: "NotificationsAlert",
    components: { IonContent, IonToggle },
    setup() {
        const { t } = useI18n();
        const talent = useUserTalent();
        const notificationsInfo = ref<NotificationsInfo>();
        const hasPermissions = ref<boolean>();

        onBeforeUnmount(() => {
            notificationsService.getNotificationsInfo().then((info) => {
                notificationsInfo.value = info;
            });
        });
        onMounted(() => {
            if (!isPlatform("capacitor")) {
                hasPermissions.value = false;
                return;
            }
            notificationsService.register().then((permissions) => {
                hasPermissions.value = permissions;
                notificationsService.getNotificationsInfo().then((info) => {
                    notificationsInfo.value = info;
                }).catch(error => {
                    console.error("Error getting notifications info:", error);
                    // Handle error appropriately
                });
            }).catch(error => {
                console.error("Error registering notifications:", error);
                // Handle error appropriately
            });
        });      
        return {
            t,
            hasPermissions,
            notificationsInfo,
            enableNotificationsChange: async (e: any) => {
                if (!talent.value?.data) {
                    return;
                }
                try {
                    if (e.target.checked) {
                        notificationsInfo.value = await notificationsService.enable();
                    } else {
                        notificationsInfo.value = await notificationsService.disable();
                    }
                } catch (error: any) {
                    errorHandlerService.handleApiError(error);
                }
            },
        };
    },
})

</script>

<style scoped>
/* Content Wrapper */
.content-wrapper {
  display: flex;
  flex-direction: column;
  min-height: 100%;
  padding: 0;
}

.scrollable-content {
  flex: 1;
  padding: 10px;
}

/* Info Section */
.info-section {
  margin-bottom: 24px;
}

.notification-header {
  display: flex;
  align-items: center;
  padding: 16px 0;
  border-bottom: 1px solid var(--loopin-color-gs-30);
}

.notification-icon {
  width: 48px;
  height: 48px;
  margin-right: 16px;
  border-radius: 50%;
  background: var(--loopin-color-blue);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 20px;
}

.notification-details {
  flex: 1;
}

.notification-title {
  font-size: 20px;
  font-weight: 700;
  margin: 0 0 4px 0;
  line-height: 1.3;
  color: var(--loopin-color-gs-100);
}

.notification-subtitle {
  font-size: 14px;
  color: var(--loopin-color-gs-60);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Status Section */
.status-section {
  margin-bottom: 24px;
}

.status-card {
  display: flex;
  align-items: center;
  padding: 16px 20px;
  border-radius: 12px;
  background: var(--loopin-item-background);
  border-left: 4px solid var(--loopin-color-gs-40);
  transition: all 0.2s ease;
}

.status-card--red {
  border-left-color: var(--loopin-color-red);
  background: linear-gradient(135deg, rgba(255, 0, 0, 0.1), var(--loopin-item-background));
}

.status-content {
  flex: 1;
}

.status-title {
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 4px;
  color: var(--loopin-color-gs-100);
}

.status-subtitle {
  font-size: 14px;
  color: var(--loopin-color-gs-60);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Details Section */
.details-section {
  margin-bottom: 24px;
}

.details-card {
  background: var(--loopin-item-background);
  border-radius: 12px;
  padding: 20px;
  border: 1px solid var(--loopin-color-gs-30);
}

.detail-item {
  margin-bottom: 20px;
}

.detail-item:last-child {
  margin-bottom: 0;
}

.detail-item h4 {
  font-size: 20px;
  font-weight: 700;
  margin: 0 0 12px 0;
  color: var(--loopin-color-gs-90);
}

.detail-item p {
  font-size: 15px;
  line-height: 1.5;
  color: var(--loopin-color-gs-70);
  margin: 0;
}

.feature-list {
  list-style: none;
  padding: 0;
  margin: 12px 0 0 0;
}

.feature-list li {
  font-size: 15px;
  line-height: 1.5;
  color: var(--loopin-color-gs-70);
  margin-bottom: 8px;
  padding-left: 20px;
  position: relative;
}

.feature-list li:before {
  content: '•';
  color: var(--loopin-color-blue);
  position: absolute;
  left: 0;
  font-weight: bold;
}

/* Toggle Section */
.toggle-section {
  margin-bottom: 24px;
}

.toggle-card {
  background: var(--loopin-item-background);
  border-radius: 12px;
  padding: 20px;
  border: 1px solid var(--loopin-color-gs-30);
}

.toggle-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.toggle-label {
  font-size: 18px;
  font-weight: 600;
  color: var(--loopin-color-gs-90);
}

/* Modal Content */
.modal-content {
  --background: var(--loopin-color-gs-10);
}

/* Responsive adjustments */
@media (max-width: 480px) {
  .notification-title {
    font-size: 18px;
  }
  
  .details-card, .toggle-card {
    padding: 16px;
  }
}
</style>
  